import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Typography,
  Box,
  Divider,
  IconButton,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useSnackbar } from "notistack";
import { useForm, Controller, useWatch } from "react-hook-form";
import { useParams } from "react-router-dom";

import {
  YES_NO_CHOICES,
  SMOKE_ALARM_TEST_FREQUENCY_OPTIONS,
  RISK_LEVEL_OPTIONS,
  initialFormData,
} from "./fireRiskAssessment.config";
import CustomSelect from "../../../../../FormFields/CustomSelect/CustomSelect";
import CustomInput from "../../../../../FormFields/CustomInput/CustomInput";
import DynamicSelectInput from "../../../../../FormFields/CustomSelect/DynamicSelectInput";
import { createFireRiskAssessment } from "../../../../../../api/services/assessment";
import { getCarePlans } from "../../../../../../api/services/core";
import { QK } from "../../../../../../base/qk";
import CustomSwitch from "../../../../../FormFields/CustomSwitch";

const SectionTitle = ({ children }) => (
  <Grid item xs={12}>
    <Typography variant="h6" sx={{ my: 2 }}>
      {children}
    </Typography>
    <Divider />
  </Grid>
);

const CreateFireRiskAssessmentModal = ({ open, handleClose }) => {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const { id: clientID } = useParams();

  const { data: carePlans } = useQuery([QK.carePlans], () => getCarePlans());

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: initialFormData,
  });

  // Watch the value of the smoking question
  const watchSmokesInHome = useWatch({
    control,
    name: "does_anyone_smoke_in_home",
  });

  const createAssessmentMutation = useMutation(
    (assessmentData) => createFireRiskAssessment(assessmentData),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([QK.fireRiskAssessments, clientID]);
        enqueueSnackbar("Fire risk assessment created successfully", {
          variant: "success",
        });
      },
      onError: (error) => {
        enqueueSnackbar(
          error?.response?.data?.message || "Failed to create assessment",
          { variant: "error" }
        );
      },
    }
  );

  const onSubmit = async (data) => {
    try {
      const payload = { ...data, client: clientID };
      await createAssessmentMutation.mutateAsync(payload);
      handleCloseModal();
    } catch (error) {
      // Error is handled by the mutation's onError callback
    }
  };

  const handleCloseModal = () => {
    reset();
    handleClose();
  };

  const requiredValidation = {
    validate: (value) =>
      (value !== null && value !== undefined) || "This field is required",
  };

  const renderQuestion = (name, label, options = YES_NO_CHOICES) => (
    <Grid item xs={12} md={12}>
      <Controller
        name={name}
        control={control}
        rules={requiredValidation}
        render={({ field }) => (
          <CustomSelect
            label={`${label} *`}
            options={options}
            {...field}
            error={!!errors[name]}
            helperText={errors[name]?.message}
            required
          />
        )}
      />
    </Grid>
  );

  return (
    <Dialog
      open={open}
      onClose={handleCloseModal}
      maxWidth="md"
      fullWidth
      scroll="body"
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Create Fire Risk Assessment</Typography>
          <IconButton onClick={handleCloseModal}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Box sx={{ p: 2 }}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12}>
                <Controller
                  name="care_plan"
                  control={control}
                  rules={{ required: "Care plan is required" }}
                  render={({ field }) => (
                    <DynamicSelectInput
                      options={carePlans}
                      label="Which care plan is this linked to? *"
                      {...field}
                      error={!!errors.care_plan}
                      helperText={errors.care_plan?.message}
                      required
                    />
                  )}
                />
              </Grid>

              <SectionTitle>Smoke Alarms &amp; Smoking</SectionTitle>
              {renderQuestion(
                "has_working_smoke_alarms",
                "Do you have working smoke alarms?"
              )}
              {renderQuestion(
                "is_accommodation_rented",
                "Is the accommodation rented?"
              )}
              {renderQuestion(
                "has_smoke_alarm_on_each_level",
                "Do you have a working smoke alarm on each level?"
              )}
              {renderQuestion(
                "smoke_alarm_test_frequency",
                "How often do you test smoke alarms?",
                SMOKE_ALARM_TEST_FREQUENCY_OPTIONS
              )}
              {renderQuestion(
                "wants_fire_brigade_referral",
                "Would you like a referral to the fire brigade?"
              )}

              {/* --- Smoking Section with CustomSwitch --- */}
              <Grid item xs={12}>
                <Controller
                  name="does_anyone_smoke_in_home"
                  control={control}
                  render={({ field }) => (
                    <CustomSwitch
                      label="Does anyone smoke in your home?"
                      name={field.name}
                      checked={field.value || false}
                      labelPlacement="start"
                      onChange={field.onChange}
                    />
                  )}
                />
              </Grid>

              {watchSmokesInHome === true && (
                <>
                  {renderQuestion(
                    "do_they_smoke_in_bed",
                    "Do they smoke in bed?"
                  )}
                  {renderQuestion(
                    "are_ashtrays_left_on_armchairs",
                    "Are ashtrays left on armchairs?"
                  )}
                </>
              )}
              {/* --- End Conditional Section --- */}

              {renderQuestion(
                "does_anyone_smoke_ecigarettes",
                "Does anyone smoke e-cigarettes?"
              )}

              <SectionTitle>Kitchen &amp; Electrical</SectionTitle>
              {renderQuestion(
                "cook_after_drinking_alcohol",
                "Does anyone cook after drinking alcohol?"
              )}
              {renderQuestion(
                "has_traditional_chip_pan",
                "Do you have a traditional chip pan?"
              )}
              {renderQuestion(
                "has_cooking_build_up",
                "Has your cooking equipment got a build-up of grease or cooking fat residue?"
              )}
              {renderQuestion(
                "is_toaster_kettle_under_cupboard",
                "Is your toaster or kettle positioned underneath a cupboard?"
              )}
              {renderQuestion(
                "switch_off_appliances_before_bed",
                "Do you switch off non-essential electrical appliances before you go to bed? (Kettle etc)"
              )}
              {renderQuestion(
                "leave_appliances_charging_overnight",
                "Do you leave electrical appliances, such as phones or computers, charging overnight?"
              )}
              {renderQuestion(
                "put_laundry_on_overnight",
                "Do you put a washing machine, tumble drier or dishwasher on overnight?"
              )}
              {renderQuestion(
                "use_electrical_devices_in_bathroom",
                "Does anyone use electrical appliances/devices in the bathroom?"
              )}
              {renderQuestion(
                "use_electric_blankets",
                "Does anyone use electric blankets?"
              )}

              <SectionTitle>Home Appliances &amp; Heating</SectionTitle>
              {renderQuestion(
                "use_block_adapters_or_extensions",
                "Does anyone use block adapters or extension leads in your home?"
              )}
              {renderQuestion(
                "use_hair_straighteners",
                "Does anyone use hair straighteners in your home?"
              )}
              {renderQuestion(
                "use_mobility_scooter",
                "Does anyone living at your home use a mobility scooter?"
              )}
              {renderQuestion(
                "use_portable_heaters",
                "Do you have portable heaters, such as fan, ceramic or gas heaters or paraffin heaters in your home?"
              )}
              {renderQuestion(
                "clothes_dried_near_fire",
                "Are clothes ever dried in front of fires or heaters? (not including radiators)"
              )}
              {renderQuestion(
                "use_wood_burning_stove",
                "Is a wood-burning stove or open fire used?"
              )}
              {renderQuestion(
                "is_chimney_swept_yearly",
                "Do you get the chimney swept every year?"
              )}
              {renderQuestion(
                "use_lit_candles_or_incense",
                "Does anyone use lit candles, incense sticks or oil burners in your home?"
              )}
              {renderQuestion(
                "use_plugin_air_fresheners",
                "Does anyone use plug-in air fresheners in your home?"
              )}
              {renderQuestion(
                "use_wheat_bags",
                "Does anyone use wheat bags in your home?"
              )}

              <SectionTitle>Home Safety &amp; Escape Plan</SectionTitle>
              {renderQuestion(
                "are_wheelie_bins_stored_away",
                "Are wheelie bins stored away after collection?"
              )}
              {renderQuestion(
                "are_internal_doors_closed_at_night",
                "Are internal doors closed at night?"
              )}
              {renderQuestion(
                "are_appliances_unplugged_at_night",
                "Are electrical appliances unplugged or switched off at night? (Unless they are designed to be left on, such as freezers)"
              )}
              {renderQuestion(
                "are_candles_left_burning",
                "Are candles, oil burners or incense burners left burning at night or when people are asleep?"
              )}
              {renderQuestion(
                "are_keys_accessible",
                "Are door and window keys kept where everyone can access them?"
              )}
              {renderQuestion(
                "has_escape_plan",
                "Is there an escape plan, so everyone in the house knows what to do if the smoke alarm goes off during the night?"
              )}
              {renderQuestion(
                "are_hallways_clear",
                "Are hallways, stairs and landings kept clear enough to allow safe and easy escape?"
              )}
              {renderQuestion(
                "can_escape_through_all_exits",
                "Would people in your home be able to escape through all exit doors and without having to search for a key?"
              )}

              <SectionTitle>Personal Factors</SectionTitle>
              {renderQuestion(
                "has_mobility_difficulties",
                "Does anyone have mobility difficulties and would require assistance to evacuate from the home?"
              )}
              {renderQuestion(
                "has_mental_illness",
                "Does anyone have a mental health illness and would require assistance to evacuate from the home?"
              )}
              {renderQuestion(
                "takes_medication_preventing_hearing_alarm",
                "Does anyone take medication that would prevent them from hearing a smoke alarm?"
              )}
              {renderQuestion(
                "has_hearing_impairment",
                "Does anyone have a hearing impairment that would prevent them from hearing a smoke alarm?"
              )}
              {renderQuestion(
                "has_visual_impairment",
                "Does anyone have a visual impairment and would be unable to escape in the event of a fire?"
              )}
              {renderQuestion(
                "consumes_alcohol_or_drugs",
                "Does anyone consume alcohol or use social drugs that would prevent them from hearing a smoke alarm?"
              )}
              {renderQuestion(
                "is_anyone_over_65",
                "Is anyone in your home over 65?"
              )}
              {renderQuestion(
                "is_anyone_under_16",
                "Is anyone in your home under 16?"
              )}
              {renderQuestion(
                "is_anyone_under_5",
                "Is anyone in your home under 5?"
              )}
              {renderQuestion(
                "does_anyone_use_oxygen_at_home",
                "Does anyone at the property use oxygen at home? (E.g. for medical purposes)"
              )}
              {renderQuestion(
                "has_arson_threat_concerns",
                "Does anyone in your home have any concerns over arson threats?"
              )}

              <SectionTitle>Final Assessment</SectionTitle>
              <Grid item xs={12}>
                <Controller
                  name="comment_recommendation"
                  control={control}
                  render={({ field }) => (
                    <CustomInput
                      label="Comment, recommendation"
                      multiline
                      rows={4}
                      {...field}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Controller
                  name="risk_level"
                  control={control}
                  rules={{ required: "Risk level is required" }}
                  render={({ field }) => (
                    <CustomSelect
                      label="Risk level *"
                      options={RISK_LEVEL_OPTIONS}
                      {...field}
                      error={!!errors.risk_level}
                      helperText={errors.risk_level?.message}
                      required
                    />
                  )}
                />
              </Grid>
            </Grid>
          </form>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={handleCloseModal} variant="outlined">
          Cancel
        </Button>
        <Button
          onClick={handleSubmit(onSubmit)}
          variant="contained"
          color="primary"
          disabled={createAssessmentMutation.isLoading}
        >
          {createAssessmentMutation.isLoading
            ? "Creating..."
            : "Create Assessment"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateFireRiskAssessmentModal;
